/**
 * Les saisies financières du mode `?demo` (ARTO-58) : tarifs, abonnements,
 * revenus, sommes dues, coûts, jalons, associés et partage, en mémoire, pour
 * vérifier les écrans sans API. Les montants sont fictifs et n'existent QUE
 * ici (comme `demoVueDAigle.ts`) : nulle part ailleurs dans la console un prix,
 * une tranche ou un pourcentage n'est écrit, et un montant non saisi vaut
 * `null`.
 *
 * Les règles de l'API que les écrans affichent sont rejouées ici, avec les
 * mêmes codes et motifs (`docs/dev/api.md` § 12.10, `saisies.ts`) : doublon,
 * créance non soldée, parts dépassées, versement existant, élévation du second
 * facteur. C'est ce qui permet de voir les erreurs sur leur ligne.
 */
import { ApiError } from './api.js';
import { demoApi } from './demoData.js';
import type {
  BeneficiaireSaisie,
  CompteAssocie,
  CorpsSaisie,
  GroupeSaisie,
  InvitationAssocie,
  RessourceSaisie,
  SaisiesParRessource,
  VersementSaisie,
  VersementsListe,
} from '../types.js';

const SUIVI = {
  createdAt: '2026-09-01T08:00:00.000Z',
  updatedAt: '2026-09-01T08:00:00.000Z',
  creeParId: null,
  modifieParId: null,
} as const;

function jour(date: string): string {
  return `${date}T00:00:00.000Z`;
}

let compteur = 0;
function nouvelId(prefixe: string): string {
  compteur += 1;
  return `${prefixe}-${compteur}`;
}

function conflit(raison: string, message: string, details: object = {}): ApiError {
  return new ApiError(409, 'conflit.version', message, { raison, ...details });
}

// ── Les six ressources ───────────────────────────────────────────────────

type Tables = { [R in RessourceSaisie]: SaisiesParRessource[R][] };

let tables: Tables | null = null;

/** Semée à la première lecture : les galeries de la démonstration existent alors. */
function semer(): Tables {
  const galeries = demoApi.listerGaleries();
  const galerie = (rang: number): string => galeries[rang]?.id ?? `demo-galerie-${rang + 1}`;
  return {
    tarifs: [
      {
        id: 'demo-tarif-1',
        type: 'palier',
        libelle: 'Essentiel',
        palier: 'essentiel',
        galeriesMin: null,
        galeriesMax: null,
        montantMensuelCentimes: 24000,
        ordre: 1,
        actif: true,
        ...SUIVI,
      },
      {
        id: 'demo-tarif-2',
        type: 'palier',
        libelle: 'Galerie',
        palier: 'galerie',
        galeriesMin: null,
        galeriesMax: null,
        montantMensuelCentimes: 39000,
        ordre: 2,
        actif: true,
        ...SUIVI,
      },
      {
        id: 'demo-tarif-3',
        type: 'palier',
        libelle: 'Groupe',
        palier: 'groupe',
        galeriesMin: null,
        galeriesMax: null,
        montantMensuelCentimes: null,
        ordre: 3,
        actif: true,
        ...SUIVI,
      },
      {
        id: 'demo-tarif-4',
        type: 'forfait_exploitant',
        libelle: 'Petit exploitant',
        palier: null,
        galeriesMin: 1,
        galeriesMax: 5,
        montantMensuelCentimes: 90000,
        ordre: 1,
        actif: true,
        ...SUIVI,
      },
      {
        id: 'demo-tarif-5',
        type: 'forfait_exploitant',
        libelle: 'Exploitant moyen',
        palier: null,
        galeriesMin: 6,
        galeriesMax: 10,
        montantMensuelCentimes: null,
        ordre: 2,
        actif: true,
        ...SUIVI,
      },
    ],
    abonnements: [
      {
        id: 'demo-abonnement-1',
        nature: 'galerie',
        groupeId: null,
        galeries: [galerie(0)],
        palier: 'galerie',
        engagementMois: 12,
        debutLe: jour('2025-11-17'),
        finLe: null,
        montantMensuelCentimes: 39000,
        devise: 'EUR',
        remisePointsDeBase: 1000,
        etat: 'actif',
        tauxTvaPointsDeBase: 2000,
        ...SUIVI,
      },
      {
        id: 'demo-abonnement-2',
        nature: 'galerie',
        groupeId: null,
        galeries: [galerie(1)],
        palier: 'essentiel',
        engagementMois: 12,
        debutLe: jour('2025-12-08'),
        finLe: null,
        montantMensuelCentimes: null,
        devise: 'EUR',
        remisePointsDeBase: 0,
        etat: 'actif',
        tauxTvaPointsDeBase: 2000,
        ...SUIVI,
      },
      {
        id: 'demo-abonnement-3',
        nature: 'galerie',
        groupeId: null,
        galeries: [galerie(2)],
        palier: 'galerie',
        engagementMois: 6,
        debutLe: jour('2026-09-01'),
        finLe: jour('2026-10-06'),
        montantMensuelCentimes: null,
        devise: 'EUR',
        remisePointsDeBase: 0,
        etat: 'essai',
        tauxTvaPointsDeBase: 2000,
        ...SUIVI,
      },
      {
        id: 'demo-abonnement-4',
        nature: 'forfait_exploitant',
        groupeId: 'demo-groupe-1',
        galeries: [galerie(0), galerie(1)],
        palier: null,
        engagementMois: 12,
        debutLe: jour('2026-03-10'),
        finLe: null,
        montantMensuelCentimes: 90000,
        devise: 'EUR',
        remisePointsDeBase: 0,
        etat: 'actif',
        tauxTvaPointsDeBase: 2000,
        ...SUIVI,
      },
    ],
    revenus: [
      {
        id: 'demo-revenu-1',
        mois: '2026-09',
        categorie: 'Formation',
        libelle: 'Deux demi-journées d’équipe',
        montantCentimes: 28000,
        service: null,
        tauxTvaPointsDeBase: 2000,
        galerieId: null,
        ...SUIVI,
      },
      {
        id: 'demo-revenu-2',
        mois: '2026-09',
        categorie: 'Mise en service',
        libelle: 'Reprise du fichier d’œuvres',
        montantCentimes: 46000,
        service: 'migration',
        tauxTvaPointsDeBase: 2000,
        galerieId: null,
        ...SUIVI,
      },
      {
        id: 'demo-revenu-3',
        mois: '2026-09',
        categorie: 'Développement sur mesure',
        libelle: 'Export comptable adapté',
        montantCentimes: null,
        service: 'integration',
        tauxTvaPointsDeBase: 2000,
        galerieId: null,
        ...SUIVI,
      },
    ],
    creances: [
      {
        id: 'demo-creance-1',
        galerieId: null,
        groupeId: null,
        debiteur: 'Galerie des Trois Fontaines',
        libelle: 'Abonnement de septembre',
        echeanceLe: jour('2026-09-15'),
        montantCentimes: 39000,
        encaisseCentimes: 0,
        etat: 'due',
        regleeLe: null,
        ...SUIVI,
      },
      {
        id: 'demo-creance-2',
        galerieId: null,
        groupeId: null,
        debiteur: 'Atelier Garance',
        libelle: 'Mise en service',
        echeanceLe: jour('2026-10-12'),
        montantCentimes: 46000,
        encaisseCentimes: 20000,
        etat: 'partielle',
        regleeLe: null,
        ...SUIVI,
      },
      {
        id: 'demo-creance-3',
        galerieId: null,
        groupeId: null,
        debiteur: 'Maison Piment',
        libelle: 'Formation d’octobre',
        echeanceLe: jour('2026-10-30'),
        montantCentimes: null,
        encaisseCentimes: 0,
        etat: 'due',
        regleeLe: null,
        ...SUIVI,
      },
    ],
    couts: [
      {
        id: 'demo-cout-1',
        mois: '2026-09',
        poste: 'Hébergement',
        montantCentimes: 82000,
        ...SUIVI,
      },
      {
        id: 'demo-cout-2',
        mois: '2026-09',
        poste: 'Stockage objet',
        montantCentimes: 34000,
        ...SUIVI,
      },
      {
        id: 'demo-cout-3',
        mois: '2026-09',
        poste: 'E-mails transactionnels',
        montantCentimes: null,
        ...SUIVI,
      },
    ],
    jalons: [
      {
        id: 'demo-jalon-1',
        libelle: 'Maquette Halo validée',
        echeanceLe: jour('2026-09-17'),
        etat: 'atteint',
        ordre: 1,
        ...SUIVI,
      },
      {
        id: 'demo-jalon-2',
        libelle: 'Démo décideur',
        echeanceLe: jour('2026-11-09'),
        etat: 'en_cours',
        ordre: 2,
        ...SUIVI,
      },
      {
        id: 'demo-jalon-3',
        libelle: 'Bascule en production',
        echeanceLe: jour('2026-12-15'),
        etat: 'a_venir',
        ordre: 3,
        ...SUIVI,
      },
    ],
  };
}

function lesTables(): Tables {
  tables ??= semer();
  return tables;
}

/** Une créance n'est réglée que soldée (`verifierCreance`, `saisies.ts`). */
function verifierCreance(fusionne: CorpsSaisie): void {
  if (fusionne['etat'] !== 'reglee') return;
  const montant = fusionne['montantCentimes'] as number | null;
  const encaisse = (fusionne['encaisseCentimes'] as number | undefined) ?? 0;
  if (montant === null) {
    throw conflit(
      'creance_non_soldee',
      'Saisissez le montant de la créance avant de la dire réglée.',
    );
  }
  if (encaisse < montant) {
    throw conflit(
      'creance_non_soldee',
      'L’encaissement ne couvre pas le montant : la créance n’est pas réglée.',
    );
  }
}

/** Un coût par poste et par mois : l'index unique de l'API. */
function verifierDoublon(
  ressource: RessourceSaisie,
  fusionne: CorpsSaisie,
  idCourant: string | null,
): void {
  if (ressource !== 'couts') return;
  const existe = lesTables().couts.some(
    (ligne) =>
      ligne.id !== idCourant &&
      ligne.mois === fusionne['mois'] &&
      ligne.poste === fusionne['poste'],
  );
  if (existe) {
    throw conflit(
      'doublon',
      'Cette saisie existe déjà : modifiez la ligne existante plutôt que d’en créer une seconde.',
    );
  }
}

// ── Associés et partage ──────────────────────────────────────────────────

let associes: CompteAssocie[] | null = null;
let beneficiaires: BeneficiaireSaisie[] | null = null;
let versements: VersementSaisie[] | null = null;

function lesAssocies(): CompteAssocie[] {
  associes ??= [
    {
      id: 'demo-venio_associe',
      email: 'solene.varenne@associes.example',
      nom: 'Solène Varenne',
      statut: 'actif',
      actif: true,
      deuxiemeFacteurActive: true,
      derniereConnexionLe: '2026-09-30T18:42:00.000Z',
      invitationEnAttente: false,
    },
    {
      id: 'demo-associe-2',
      email: 'armand.querel@associes.example',
      nom: 'Armand Quérel',
      statut: 'actif',
      actif: true,
      deuxiemeFacteurActive: true,
      derniereConnexionLe: '2026-09-28T09:15:00.000Z',
      invitationEnAttente: false,
    },
    {
      id: 'demo-associe-3',
      email: 'ines.delarocque@associes.example',
      nom: 'Inès Delarocque',
      statut: 'actif',
      actif: false,
      deuxiemeFacteurActive: true,
      derniereConnexionLe: '2026-09-12T21:03:00.000Z',
      invitationEnAttente: false,
    },
    {
      id: 'demo-associe-4',
      email: 'bastien.orfevre@associes.example',
      nom: 'Bastien Orfèvre',
      statut: 'invite',
      actif: true,
      deuxiemeFacteurActive: false,
      derniereConnexionLe: null,
      invitationEnAttente: true,
    },
  ];
  return associes;
}

function lesBeneficiaires(): BeneficiaireSaisie[] {
  beneficiaires ??= [
    {
      id: 'demo-beneficiaire-1',
      libelle: 'Solène Varenne',
      compteId: 'demo-venio_associe',
      partPointsDeBase: 2000,
      recoitLeReste: false,
      galeriesExclues: [],
    },
    {
      id: 'demo-beneficiaire-2',
      libelle: 'Armand Quérel',
      compteId: 'demo-associe-2',
      partPointsDeBase: 1500,
      recoitLeReste: false,
      galeriesExclues: [],
    },
    {
      id: 'demo-beneficiaire-3',
      libelle: 'Réserve de l’entreprise',
      compteId: null,
      partPointsDeBase: null,
      recoitLeReste: true,
      galeriesExclues: [],
    },
  ];
  return beneficiaires;
}

function lesVersements(): VersementSaisie[] {
  versements ??= [
    {
      id: 'demo-versement-1',
      beneficiaireId: 'demo-beneficiaire-1',
      mois: '2026-09',
      montantDuCentimes: 127600,
      verseCentimes: 0,
      verseLe: null,
    },
    {
      id: 'demo-versement-2',
      beneficiaireId: 'demo-beneficiaire-2',
      mois: '2026-08',
      montantDuCentimes: null,
      verseCentimes: 0,
      verseLe: null,
    },
  ];
  return versements;
}

/** L'élévation du second facteur : valable quinze minutes, comme l'API. */
const FENETRE_STEP_UP_MS = 15 * 60_000;
let verifieeLe = 0;

export function exigerElevationDemo(): void {
  exigerElevation();
}

function exigerElevation(): void {
  if (Date.now() - verifieeLe <= FENETRE_STEP_UP_MS) return;
  throw new ApiError(
    403,
    'acces.refuse',
    'Une vérification récente du second facteur est requise.',
    { raison: 'mfa_step_up_requis' },
  );
}

function sommeDesParts(sauf: string | null = null): number {
  return lesBeneficiaires().reduce(
    (total, ligne) => total + (ligne.id === sauf ? 0 : (ligne.partPointsDeBase ?? 0)),
    0,
  );
}

function introuvable(libelle: string): ApiError {
  return new ApiError(404, 'ressource.introuvable', `${libelle} introuvable.`);
}

export const demoSaisies = {
  /** Deux exploitants fictifs, qui se partagent les galeries de la démonstration. */
  listerGroupes(): GroupeSaisie[] {
    const galeries = demoApi
      .listerGaleries()
      .map((galerie) => ({ id: galerie.id, nom: galerie.nom }));
    return [
      { id: 'demo-groupe-1', nom: 'Ateliers du Quai', galeries: galeries.slice(0, 2) },
      { id: 'demo-groupe-2', nom: 'Réseau Lumière', galeries: galeries.slice(2) },
    ];
  },

  lire<R extends RessourceSaisie>(ressource: R): SaisiesParRessource[R][] {
    return lesTables()[ressource].map((ligne) => ({ ...ligne }));
  },

  creer<R extends RessourceSaisie>(ressource: R, corps: CorpsSaisie): SaisiesParRessource[R] {
    verifierDoublon(ressource, corps, null);
    if (ressource === 'creances') verifierCreance(corps);
    const defauts: Record<RessourceSaisie, CorpsSaisie> = {
      tarifs: { palier: null, galeriesMin: null, galeriesMax: null, montantMensuelCentimes: null },
      abonnements: {
        nature: 'galerie',
        groupeId: null,
        palier: null,
        finLe: null,
        montantMensuelCentimes: null,
        devise: 'EUR',
        remisePointsDeBase: 0,
        tauxTvaPointsDeBase: 2000,
      },
      revenus: { montantCentimes: null, service: null, tauxTvaPointsDeBase: 2000 },
      creances: { galerieId: null, groupeId: null, montantCentimes: null, encaisseCentimes: 0 },
      couts: { montantCentimes: null },
      jalons: { echeanceLe: null, ordre: 0 },
    };
    const ligne = {
      ...defauts[ressource],
      ...corps,
      id: nouvelId(`demo-${ressource}`),
      ...SUIVI,
    } as unknown as SaisiesParRessource[R];
    // Les champs de type date arrivent en `AAAA-MM-JJ` : la forme JSON de l'API est ISO.
    for (const [cle, valeur] of Object.entries(ligne)) {
      if (/Le$/.test(cle) && typeof valeur === 'string' && valeur.length === 10) {
        (ligne as Record<string, unknown>)[cle] = jour(valeur);
      }
    }
    (lesTables()[ressource] as SaisiesParRessource[R][]).unshift(ligne);
    return { ...ligne };
  },

  modifier<R extends RessourceSaisie>(
    ressource: R,
    id: string,
    corps: CorpsSaisie,
  ): SaisiesParRessource[R] {
    const liste = lesTables()[ressource] as SaisiesParRessource[R][];
    const position = liste.findIndex((ligne) => ligne.id === id);
    const actuelle = liste[position];
    if (!actuelle) throw introuvable('Saisie');
    const fusionne = { ...actuelle, ...corps } as unknown as CorpsSaisie;
    verifierDoublon(ressource, fusionne, id);
    if (ressource === 'creances') verifierCreance(fusionne);
    for (const [cle, valeur] of Object.entries(fusionne)) {
      if (/Le$/.test(cle) && typeof valeur === 'string' && valeur.length === 10) {
        fusionne[cle] = jour(valeur);
      }
    }
    const modifiee = fusionne as unknown as SaisiesParRessource[R];
    liste[position] = modifiee;
    return { ...modifiee };
  },

  supprimer(ressource: RessourceSaisie, id: string): void {
    const liste = lesTables()[ressource] as { id: string }[];
    const position = liste.findIndex((ligne) => ligne.id === id);
    if (position < 0) throw introuvable('Saisie');
    liste.splice(position, 1);
  },

  // ── Second facteur ─────────────────────────────────────────────────────

  /** N'importe quel code non vide est accepté : la démonstration ne tient aucun secret. */
  verifierDeuxFacteurs(code: string): void {
    if (code.trim() === '') {
      throw new ApiError(401, 'code_invalide', 'Ce code n’est pas valide. Réessayez.');
    }
    verifieeLe = Date.now();
  },

  // ── Associés ───────────────────────────────────────────────────────────

  listerAssocies(): CompteAssocie[] {
    return lesAssocies().map((compte) => ({ ...compte }));
  },

  inviterAssocie(corps: { email: string; nom: string }): InvitationAssocie {
    exigerElevation();
    const email = corps.email.trim().toLowerCase();
    if (lesAssocies().some((compte) => compte.email === email)) {
      throw conflit(
        'invitation_concurrente',
        'Cette adresse vient d’être invitée : rechargez la liste.',
      );
    }
    const id = nouvelId('demo-associe');
    lesAssocies().push({
      id,
      email,
      nom: corps.nom.trim(),
      statut: 'invite',
      actif: true,
      deuxiemeFacteurActive: false,
      derniereConnexionLe: null,
      invitationEnAttente: true,
    });
    return {
      id,
      invitationExpireLe: new Date(Date.now() + 7 * 86_400_000).toISOString(),
      invitationEnvoyee: true,
    };
  },

  modifierAssocie(id: string, corps: { actif?: boolean; reinitialiserDeuxFacteurs?: true }): void {
    exigerElevation();
    const compte = lesAssocies().find((ligne) => ligne.id === id);
    if (!compte || compte.invitationEnAttente) throw introuvable('Associé');
    if (corps.actif !== undefined) compte.actif = corps.actif;
    if (corps.reinitialiserDeuxFacteurs === true) compte.deuxiemeFacteurActive = false;
  },

  annulerInvitationAssocie(id: string): void {
    exigerElevation();
    const liste = lesAssocies();
    const rang = liste.findIndex((ligne) => ligne.id === id && ligne.invitationEnAttente);
    if (rang === -1) throw introuvable('Invitation');
    // Comme l'API : plus d'invitation ouverte, le compte sort de la liste.
    liste.splice(rang, 1);
  },

  // ── Partage ────────────────────────────────────────────────────────────

  listerBeneficiaires(): BeneficiaireSaisie[] {
    return lesBeneficiaires().map((ligne) => ({ ...ligne }));
  },

  creerBeneficiaire(
    corps: Omit<BeneficiaireSaisie, 'id' | 'recoitLeReste' | 'galeriesExclues'> & {
      recoitLeReste?: boolean;
    },
  ): { id: string } {
    const part = corps.partPointsDeBase ?? 0;
    if (sommeDesParts() + part > 10_000) throw partsDepassees(sommeDesParts() + part);
    if (corps.recoitLeReste === true && lesBeneficiaires().some((b) => b.recoitLeReste)) {
      throw resteDejaDesigne();
    }
    const id = nouvelId('demo-beneficiaire');
    lesBeneficiaires().push({
      ...corps,
      recoitLeReste: corps.recoitLeReste ?? false,
      galeriesExclues: [],
      id,
    });
    return { id };
  },

  modifierBeneficiaire(id: string, corps: Partial<Omit<BeneficiaireSaisie, 'id'>>): { id: string } {
    const ligne = lesBeneficiaires().find((candidat) => candidat.id === id);
    if (!ligne) throw introuvable('Bénéficiaire');
    const part =
      corps.partPointsDeBase === undefined ? ligne.partPointsDeBase : corps.partPointsDeBase;
    const somme = sommeDesParts(id) + (part ?? 0);
    if (somme > 10_000) throw partsDepassees(somme);
    if (corps.recoitLeReste === true) {
      if (lesBeneficiaires().some((b) => b.id !== id && b.recoitLeReste)) {
        throw resteDejaDesigne();
      }
      if (ligne.galeriesExclues.length > 0) {
        throw repartitionInvalide(
          'reste_avec_exclusions',
          'Ce bénéficiaire exclut des galeries : retirez ses exclusions avant de lui donner le reste.',
        );
      }
    }
    Object.assign(ligne, corps);
    return { id };
  },

  /** `PUT /partage/reste` : le nouveau validé avant que l'ancien ne le perde. */
  transfererReste(beneficiaireId: string): void {
    const ligne = lesBeneficiaires().find((candidat) => candidat.id === beneficiaireId);
    if (!ligne) throw introuvable('Bénéficiaire');
    if (ligne.galeriesExclues.length > 0) {
      throw repartitionInvalide(
        'reste_avec_exclusions',
        'Ce bénéficiaire exclut des galeries : retirez ses exclusions avant de lui donner le reste.',
      );
    }
    for (const autre of lesBeneficiaires()) autre.recoitLeReste = autre.id === beneficiaireId;
  },

  ajouterExclusion(beneficiaireId: string, corps: { galerieId: string; depuisMois: string }): void {
    const ligne = lesBeneficiaires().find((candidat) => candidat.id === beneficiaireId);
    if (!ligne) throw introuvable('Bénéficiaire');
    if (ligne.recoitLeReste) {
      throw repartitionInvalide(
        'exclusion_du_reste',
        'Ce bénéficiaire reçoit le reste : il ne peut pas exclure de galerie.',
      );
    }
    if (ligne.galeriesExclues.some((e) => e.galerieId === corps.galerieId)) {
      throw conflit(
        'exclusion_existante',
        'Ce bénéficiaire exclut déjà cette galerie : retirez l’exclusion pour en changer le mois.',
      );
    }
    ligne.galeriesExclues.push({ galerieId: corps.galerieId, depuisMois: corps.depuisMois });
  },

  retirerExclusion(beneficiaireId: string, galerieId: string): void {
    const ligne = lesBeneficiaires().find((candidat) => candidat.id === beneficiaireId);
    if (!ligne) throw introuvable('Bénéficiaire');
    const avant = ligne.galeriesExclues.length;
    ligne.galeriesExclues = ligne.galeriesExclues.filter((e) => e.galerieId !== galerieId);
    if (ligne.galeriesExclues.length === avant) throw introuvable('Exclusion');
  },

  supprimerBeneficiaire(id: string): void {
    if (lesVersements().some((versement) => versement.beneficiaireId === id)) {
      throw conflit(
        'versements_existants',
        'Ce bénéficiaire a des versements : il ne peut pas être supprimé.',
      );
    }
    const liste = lesBeneficiaires();
    const position = liste.findIndex((ligne) => ligne.id === id);
    if (position < 0) throw introuvable('Bénéficiaire');
    liste.splice(position, 1);
  },

  listerVersements(mois?: string): VersementsListe {
    // La clôture de la démo n'écrit pas les dus des versements : elle n'en fige aucun.
    return {
      items: lesVersements()
        .filter((ligne) => mois === undefined || ligne.mois === mois)
        .map((ligne) => ({ ...ligne })),
      moisClotures: [],
    };
  },

  creerVersement(corps: Omit<VersementSaisie, 'id'>): { id: string } {
    if (
      lesVersements().some(
        (ligne) => ligne.beneficiaireId === corps.beneficiaireId && ligne.mois === corps.mois,
      )
    ) {
      throw conflit(
        'versement_existant',
        'Un versement existe déjà pour ce bénéficiaire et ce mois : modifiez-le.',
      );
    }
    const id = nouvelId('demo-versement');
    lesVersements().push({
      ...corps,
      id,
      verseLe: corps.verseLe === null ? null : jour(corps.verseLe.slice(0, 10)),
    });
    return { id };
  },

  modifierVersement(id: string, corps: Partial<Omit<VersementSaisie, 'id'>>): { id: string } {
    const ligne = lesVersements().find((candidat) => candidat.id === id);
    if (!ligne) throw introuvable('Versement');
    Object.assign(ligne, corps);
    if (typeof ligne.verseLe === 'string') ligne.verseLe = jour(ligne.verseLe.slice(0, 10));
    return { id };
  },
};

function resteDejaDesigne(): ApiError {
  return conflit(
    'reste_deja_designe',
    'Un autre bénéficiaire reçoit déjà le reste : retirez-le-lui d’abord.',
  );
}

function repartitionInvalide(raison: string, message: string): ApiError {
  return new ApiError(422, 'comptabilite.repartition_invalide', message, { raison });
}

function partsDepassees(somme: number): ApiError {
  return conflit(
    'parts_depassees',
    'La somme des parts dépasserait 100 % : baissez d’abord une autre part.',
    { somme, plafond: 10_000 },
  );
}
